import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { auth } from '../../../config/firebase';
import { TrendingUp, TrendingDown, ArrowRightLeft, Plus, Filter } from 'lucide-react';
import './StockTransactionsTab.css';

const StockTransactionsTab = ({ hotelId, token }) => {
  const effectiveToken = token || localStorage.getItem('token') || localStorage.getItem('authToken') || sessionStorage.getItem('token');
  const [transactions, setTransactions] = useState([]);
  const [items, setItems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [transactionType, setTransactionType] = useState('stock_in');
  const [formData, setFormData] = useState({
    item_id: '',
    quantity: '',
    unit_price: '',
    department: '',
    notes: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const transactionTypes = [
    { value: 'stock_in', label: 'Stock In', icon: TrendingUp, class: 'type-in' },
    { value: 'stock_out', label: 'Stock Out', icon: TrendingDown, class: 'type-out' },
    { value: 'wastage', label: 'Wastage', icon: TrendingDown, class: 'type-wastage' },
    { value: 'adjustment', label: 'Adjustment', icon: ArrowRightLeft, class: 'type-adjustment' }
  ];

  const getAuthHeaders = async (forceRefresh = false) => {
    const currentUser = auth.currentUser;

    if (currentUser) {
      const idToken = await currentUser.getIdToken(forceRefresh);
      return { Authorization: `Bearer ${idToken}` };
    }

    const fallbackToken =
      token ||
      localStorage.getItem('authToken') ||
      sessionStorage.getItem('authToken') ||
      localStorage.getItem('token') ||
      sessionStorage.getItem('token');

    if (!fallbackToken) {
      throw new Error('Authentication token unavailable');
    }

    const sanitized = String(fallbackToken).replace(/^Bearer\s+/i, '').replace(/^"+|"+$/g, '').trim();
    return { Authorization: `Bearer ${sanitized}` };
  };

  const requestWithAuth = async (requestFactory) => {
    try {
      const headers = await getAuthHeaders(false);
      return await requestFactory(headers);
    } catch (err) {
      if (err?.response?.status === 401 && auth.currentUser) {
        const headers = await getAuthHeaders(true);
        return requestFactory(headers);
      }
      throw err;
    }
  };

  useEffect(() => {
    if (hotelId && effectiveToken) {
      fetchTransactions();
      fetchItems();
      fetchDepartments();
    }
  }, [hotelId, effectiveToken, filterType]);

  const fetchTransactions = async () => {
    try {
      const queryParams = new URLSearchParams({ hotel_id: String(hotelId) });
      if (filterType) {
        queryParams.set('transaction_type', filterType);
      }

      const url = `${process.env.REACT_APP_API_URL}/api/inventory/stock-transactions?${queryParams.toString()}`;
      
      const response = await requestWithAuth((headers) => axios.get(url, { headers }));
      const rows = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
          ? response.data
          : [];
      setTransactions(rows);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError(err.response?.data?.message || 'Failed to load transactions');
      setLoading(false);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await requestWithAuth((headers) => axios.get(
        `${process.env.REACT_APP_API_URL}/api/inventory/items?hotel_id=${hotelId}`,
        { headers }
      ));
      const rows = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
          ? response.data
          : [];
      setItems(rows);
    } catch (err) {
      console.error('Error fetching items:', err);
      setError(err.response?.data?.message || 'Failed to load items');
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await requestWithAuth((headers) => axios.get(
        `${process.env.REACT_APP_API_URL}/api/departments`,
        { headers }
      ));
      const rows = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
          ? response.data
          : [];

      setDepartments(rows.map((department) => ({
        id: department.department_id,
        name: department.department_name
      })));
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const payload = {
        hotel_id: hotelId,
        item_id: parseInt(formData.item_id),
        quantity: parseFloat(formData.quantity),
        ...(transactionType === 'stock_in' ? { unit_price: parseFloat(formData.unit_price) } : {}),
        department: formData.department,
        notes: formData.notes
      };

      const endpoint = transactionType === 'wastage'
        ? `/api/inventory/stock-transactions/wastage`
        : transactionType === 'adjustment'
          ? `/api/inventory/stock-transactions/adjustment`
          : transactionType === 'stock_in'
            ? `/api/inventory/stock-transactions/stock-in`
            : `/api/inventory/stock-transactions/stock-out`;

      await requestWithAuth((headers) => axios.post(
        `${process.env.REACT_APP_API_URL}${endpoint}`,
        payload,
        { headers }
      ));
      
      setSuccess(`${transactionTypes.find(t => t.value === transactionType)?.label} recorded successfully`);
      fetchTransactions();
      resetForm();
    } catch (err) {
      console.error('Error creating transaction:', err);
      setError(err.response?.data?.message || 'Failed to create transaction');
    }
  };

  const resetForm = () => {
    setFormData({
      item_id: '',
      quantity: '',
      unit_price: '',
      department: '',
      notes: ''
    });
    setShowForm(false);
  };

  const handleItemChange = (itemId) => {
    const selectedItem = items.find((item) => String(item.item_id) === String(itemId));
    setFormData({
      ...formData,
      item_id: itemId,
      unit_price: transactionType === 'stock_in' && selectedItem?.cost_price != null
        ? String(selectedItem.cost_price)
        : formData.unit_price
    });
  };

  const getTransactionIcon = (type) => {
    const config = transactionTypes.find(t => t.value === type);
    if (!config) return null;
    const Icon = config.icon;
    return <Icon size={20} className={config.class} />;
  };

  if (loading) {
    return (
      <div className="tab-loading">
        <div className="loading-spinner"></div>
        <p>Loading stock transactions...</p>
      </div>
    );
  }

  return (
    <div className="stock-transactions-tab">
      <div className="tab-header">
        <h2><TrendingUp size={24} /> Stock Transactions</h2>
        <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
          <Plus size={20} /> {showForm ? 'Cancel' : 'New Transaction'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {showForm && (
        <div className="form-container">
          <h3>New Stock Transaction</h3>
          
          <div className="transaction-type-selector">
            {transactionTypes.map(type => (
              <button
                key={type.value}
                type="button"
                className={`type-button ${transactionType === type.value ? 'active' : ''} ${type.class}`}
                onClick={() => setTransactionType(type.value)}
              >
                {React.createElement(type.icon, { size: 20 })}
                {type.label}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Item *</label>
              <select
                value={formData.item_id}
                onChange={(e) => handleItemChange(e.target.value)}
                required
              >
                <option value="">Select Item</option>
                {items.map(item => (
                  <option key={item.item_id} value={item.item_id}>
                    {item.item_name} (Stock: {item.current_quantity ?? item.quantity_on_hand ?? 0} {item.unit ?? item.unit_of_measurement ?? ''})
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Quantity *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={formData.quantity}
                  onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                  required
                  placeholder="0.00"
                />
              </div>

              {transactionType === 'stock_in' && (
                <div className="form-group">
                  <label>Unit Price *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.unit_price}
                    onChange={(e) => setFormData({...formData, unit_price: e.target.value})}
                    required
                    placeholder="0.00"
                  />
                </div>
              )}

              <div className="form-group">
                <label>Department{transactionType === 'stock_out' ? ' *' : ''}</label>
                <select
                  value={formData.department}
                  onChange={(e) => setFormData({...formData, department: e.target.value})}
                  required={transactionType === 'stock_out'}
                >
                  <option value="">Select Department</option>
                  {departments.map((department) => (
                    <option key={department.id} value={department.name}>
                      {department.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={3}
                placeholder="Additional information"
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                Record {transactionTypes.find(t => t.value === transactionType)?.label}
              </button>
              <button type="button" className="btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="filters-bar">
        <div className="filter-group">
          <Filter size={20} />
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="">All Transactions</option>
            {transactionTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="transactions-table-container">
        <table className="transactions-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Item</th>
              <th>Quantity</th>
              <th>Department</th>
              <th>Performed By</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((transaction) => (
              <tr key={transaction.transaction_id}>
                <td>{new Date(transaction.transaction_date).toLocaleString()}</td>
                <td>
                  <div className="transaction-type">
                    {getTransactionIcon(transaction.transaction_type)}
                    <span>{transaction.transaction_type}</span>
                  </div>
                </td>
                <td><strong>{transaction.item_name}</strong></td>
                <td>
                  <span className={transaction.transaction_type === 'stock_in' ? 'quantity-positive' : 'quantity-negative'}>
                    {transaction.transaction_type === 'stock_in' ? '+' : '-'}{transaction.quantity} {transaction.unit || transaction.unit_of_measurement || ''}
                  </span>
                </td>
                <td>{transaction.department || '-'}</td>
                <td>{transaction.performed_by_name || 'System'}</td>
                <td>{transaction.notes || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {transactions.length === 0 && (
        <div className="empty-state">
          <TrendingUp size={48} />
          <h3>No Transactions</h3>
          <p>Stock movements will appear here</p>
        </div>
      )}
    </div>
  );
};

export default StockTransactionsTab;
